/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router HOC and shared utilities */
import { withRouter, utils, isTokenValid, applySeo, buildOrganizationLd, SITE_URL } from "../helpers"

/* Endpoints and the short link service */
import { env } from "../constants"

/* Shared UI components */
import { Block, Loading, Network, Stars } from "../components"

/* Icon component */
import Icon from "../components/Icon"

/* Organization API: card, services, specialists, reviews, phone reveal */
import { get, services, specialists, reviews, phone as revealPhone } from "../api/Organization"

/* Review API: create or update a review */
import { create } from "../api/Review"

/* Favourite API: add / remove from favourites */
import { add, remove } from "../api/Favourite"

/* Queue API: join, position, leave */
import { join, position as queuePositionApi, leave } from "../api/Queue"

/* Translation helper */
import { translate } from "../translates"

/* Gallery lightbox widget */
import Lightbox from "../widgets/organization/Lightbox"

/* Leave / edit review modal widget */
import LeaveReview from "../widgets/organization/LeaveReview"


/* Page Organization */
class Organization extends Component {

    constructor(props) {
        super(props)
        /* State */
        this.state = {
            loading: true,
            error: false,
            organization: null,
            services: [],
            serviceGroups: [],
            serviceType: "default",
            specialists: [],
            reviews: { summary: {}, data: [] },
            myReview: null,
            selected: [],
            tab: "services",
            galleryAt: -1,
            phoneShown: false,
            phoneFull: null,
            phoneLoading: false,
            hoursOpen: false,
            mapOpen: false,
            leaveOpen: false,
            reviewsOpen: false,
            modalReviews: [],
            modalFilter: null,
            modalPage: 1,
            modalHasMore: false,
            modalLoading: false,
            modalFiltering: false,
            reviewPhotos: null,
            reviewPhotoAt: 0,
            queuePosition: null,
            queueBusy: false,
            toast: null
        }
    }

    /* Draw Load Queue */
    _loadQueue = (organizationId) => {
        if (!this.props.token) {
            return
        }
        queuePositionApi(this.props.token, organizationId).then((response) => {
            if (response && response.status === 200) {
                this.setState({ queuePosition: response.data })
            }
        })
    }

    /* Require a live session before a protected action. An expired token is dropped so the
       header stops showing "authorized", then the login modal opens in place (no redirect). */
    _authorized = () => {
        if (isTokenValid(this.props.token)) {
            return true
        }
        if (this.props.token) {
            this.props.removeToken()
        }
        this.props.toggleAuth(true)
        return false
    }

    /* Draw Join Queue */
    _joinQueue = () => {
        /* Fields */
        const organization = this.state.organization
        if (!this._authorized()) {
            return
        }
        /* A service must be chosen before joining the queue */
        if (this.state.queueBusy || this.state.selected.length === 0) {
            return
        }
        this.setState({ queueBusy: true })
        join(this.props.token, { organizationId: organization.id, serviceIds: this.state.selected }).then((response) => {
            this.setState({ queueBusy: false })
            if (response && (response.status === 201 || response.status === 200)) {
                this.setState({ queuePosition: response.data })
            }
        })
    }

    /* Draw Leave Queue */
    _leaveQueue = () => {
        /* Fields */
        const { token } = this.props
        const organization = this.state.organization
        if (this.state.queueBusy) {
            return
        }
        this.setState({ queueBusy: true })
        leave(token, organization.id).then(() => {
            this.setState({ queueBusy: false, queuePosition: null })
        })
    }

    componentDidMount = () => {
        /* Open the page from the top — router keeps the previous page's scroll otherwise */
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prev) => {
        if (prev.params.url !== this.props.params.url) {
            window.scrollTo(0, 0)
            this.load()
        }
    }

    load = () => {
        /* The route param is a url slug (or numeric id) — resolve the organization first, then load the rest by its numeric id */
        const key = this.props.params.url
        this.setState({ loading: true, error: false })
        get(key).then((resOrg) => {
            if (!resOrg || resOrg.status !== 200) {
                this.setState({ loading: false, error: true })
                return
            }
            const organization = resOrg.data
            const id = organization.id
            this._seo(organization)
            Promise.all([services(id), specialists(id), reviews(id, {}, this.props.token)]).then(([resServices, resSpec, resReviews]) => {
                /* Services come either as a flat array (legacy) or { serviceType, services, groups } */
                const serviceData = resServices && resServices.status === 200 ? resServices.data : null
                const svc = serviceData ? (Array.isArray(serviceData) ? serviceData : (serviceData.services || [])) : []
                const serviceGroups = serviceData && !Array.isArray(serviceData) ? (serviceData.groups || []) : []
                const serviceType = serviceData && !Array.isArray(serviceData) ? (serviceData.serviceType || "default") : "default"
                const reviewsData = resReviews && resReviews.status === 200 ? resReviews.data : { summary: {}, data: [] }

                /* Restore any services carried back in the URL (e.g. when the booking modal is closed) */
                const preselect = (new URLSearchParams(this.props.location.search).get("services") || "").split(",").filter((item) => item)
                const selected = svc.filter((item) => preselect.includes(`${item.id}`)).map((item) => item.id)

                this.setState({
                    organization,
                    services: svc,
                    serviceGroups,
                    serviceType,
                    specialists: resSpec && resSpec.status === 200 ? resSpec.data : [],
                    reviews: reviewsData,
                    myReview: reviewsData.mine || null,
                    selected,
                    loading: false
                }, () => {
                    /* Show the visitor's own queue position if they're already waiting */
                    if (organization.queue) {
                        this._loadQueue(organization.id)
                    }
                })
            })
        })
    }

    /* Draw Seo */
    _seo = (organization) => {
        /* Fields */
        const category = organization.category
        const gallery = Array.isArray(organization.gallery) && organization.gallery.length ? organization.gallery : []
        const slug = organization.url || organization.id
        const url = `${SITE_URL}/organization/${slug}`
        const image = gallery.length ? utils.media(gallery[0]) : undefined

        /* Title and description built from the real organization data */
        const categoryPart = category ? ` · ${category.name}` : ""
        const title = `${organization.name} — онлайн-запись${categoryPart} | Bookify`
        const ratingPart = organization.rating ? `Рейтинг ${Number(organization.rating).toFixed(1)}★${organization.reviewsCount ? ` (${organization.reviewsCount})` : ""}. ` : ""
        const addressPart = organization.addr ? `, ${organization.addr}` : ""
        const description = `Запишитесь онлайн в «${organization.name}»${category ? ` — ${category.name}` : ""}${addressPart}. ${ratingPart}Выберите услугу, мастера и удобное время в Bookify.`

        /* Breadcrumb — Home → category → organization */
        const breadcrumb = [
            { name: "Главная", url: `${SITE_URL}/` },
            { name: category ? category.name : "Каталог", url: category ? `${SITE_URL}/search?category=${category.id}` : `${SITE_URL}/search` },
            { name: organization.name, url }
        ]

        applySeo({
            title,
            description,
            url,
            image,
            ogType: "business.business",
            jsonLd: buildOrganizationLd(organization, url, image),
            breadcrumb
        })
    }

    /* Draw Is Fav */
    _isFav = () => {
        const id = this.state.organization.id
        return Array.isArray(this.props.favourites) && this.props.favourites.some((item) => item.id === id)
    }

    /* Draw Favourite */
    _favourite = () => {
        if (!this._authorized()) {
            return
        }
        const organization = this.state.organization
        if (this._isFav()) {
            remove(this.props.token, organization.id)
            this.props.removeFavourite(organization)
        }
        else {
            add(this.props.token, organization.id)
            this.props.addFavourite(organization)
        }
    }

    /* Draw Toggle Service */
    _toggleService = (id) => {
        const selected = this.state.selected.includes(id) ? this.state.selected.filter((item) => item !== id) : [...this.state.selected, id]
        this.setState({ selected })
    }

    /* Draw Share */
    _share = () => {
        const s = this.state.organization

        /* The short link of the point — its readable address stands in it, and a point that has
           none left keeps the old link by its uid, which opens the same card */
        const token = s.url ? s.url : s.uid
        const link = `${env.shareLink}/g/${token}?source=share`
        const text = `${s.name} · ${s.addr}\n${link}`
        const area = document.createElement("textarea")
        area.value = text
        area.style.position = "fixed"
        area.style.opacity = "0"
        document.body.appendChild(area)
        area.select()
        try { document.execCommand("copy") } catch (error) { /* Clipboard copy is best-effort */ }
        document.body.removeChild(area)
        this.setState({ toast: translate("Ссылка скопирована") })
        setTimeout(() => this.setState({ toast: null }), 1800)
    }

    /* Draw Book */
    _book = () => {
        if (!this._authorized()) {
            return
        }
        const ids = this.state.selected.join(",")
        /* Carry the booking channel (?source=) from a share/QR/2gis link through to the booking page */
        const source = new URLSearchParams(this.props.location.search).get("source")
        const channel = source ? `&source=${encodeURIComponent(source)}` : ""
        this.props.navigate(`/booking?organization=${this.state.organization.id}&services=${ids}${channel}`)
    }

    /* Draw Reveal Phone */
    _revealPhone = () => {
        if (this.state.phoneLoading || this.state.phoneShown) {
            return
        }
        this.setState({ phoneLoading: true })
        revealPhone(this.state.organization.id).then((response) => {
            if (response && response.status === 200) {
                this.setState({ phoneShown: true, phoneFull: response.data.phone, phoneLoading: false })
            }
            else {
                this.setState({ phoneLoading: false })
            }
        })
    }

    /* Draw Call */
    _call = () => {
        if (this.state.phoneFull) {
            window.location.href = `tel:${this.state.phoneFull.replace(/\D/g, "")}`
            return
        }
        if (this.state.phoneLoading) {
            return
        }
        this.setState({ phoneLoading: true })
        revealPhone(this.state.organization.id).then((response) => {
            if (response && response.status === 200) {
                this.setState({ phoneShown: true, phoneFull: response.data.phone, phoneLoading: false })
                window.location.href = `tel:${(response.data.phone || "").replace(/\D/g, "")}`
            }
            else {
                this.setState({ phoneLoading: false })
            }
        })
    }

    /* Draw Open Leave Review */
    _openLeaveReview = () => {
        if (!this._authorized()) {
            return
        }
        this.setState({ leaveOpen: true, reviewsOpen: false })
    }

    /* Draw Submit Review */
    _submitReview = (data) => {
        const wasEditing = !!this.state.myReview
        return create(this.props.token, { organizationId: this.state.organization.id, memberId: data.memberId, rating: data.rating, text: data.text, photoIds: data.photoIds }).then((response) => {
            if (!response || (response.status !== 200 && response.status !== 201)) {
                this.setState({ leaveOpen: false, toast: translate("Не удалось сохранить отзыв") })
                setTimeout(() => this.setState({ toast: null }), 2500)
                return
            }
            const review = response.data
            const replaceIn = (list) => list.map((item) => item.id === review.id ? review : item)
            const summary = { ...this.state.reviews.summary }
            if (!wasEditing) {
                summary.total = (summary.total || 0) + 1
            }
            this.setState({
                leaveOpen: false,
                reviews: { summary, data: wasEditing ? replaceIn(this.state.reviews.data || []) : [review, ...(this.state.reviews.data || [])] },
                modalReviews: wasEditing ? replaceIn(this.state.modalReviews) : [review, ...this.state.modalReviews],
                myReview: { id: review.id, rating: review.rating, text: review.text, photos: review.photos, masterId: review.masterId, master: review.master },
                toast: wasEditing ? translate("Отзыв обновлён") : translate("Ваш отзыв опубликован")
            })
            setTimeout(() => this.setState({ toast: null }), 2500)
        })
    }

    /* Draw Distribution */
    _distribution = () => (this.state.reviews.summary && this.state.reviews.summary.distribution) || {}

    /* Draw Reviews Total */
    _reviewsTotal = () => (this.state.reviews.summary && this.state.reviews.summary.total) || (this.state.reviews.data || []).length

    /* Draw Open Reviews */
    _openReviews = () => {
        const data = this.state.reviews.data || []
        this.setState({ reviewsOpen: true, modalReviews: data, modalFilter: null, modalPage: 1, modalHasMore: data.length < this._reviewsTotal() })
    }

    /* Draw Filter Reviews */
    _filterReviews = (rating) => {
        if (this.state.modalFilter === rating || this.state.modalLoading) {
            return
        }
        this.setState({ modalLoading: true, modalFiltering: true, modalFilter: rating })
        reviews(this.state.organization.id, rating ? { rating } : {}).then((response) => {
            if (response && response.status === 200) {
                const data = Array.isArray(response.data.data) ? response.data.data : []
                const expected = rating ? (this._distribution()[rating] || 0) : this._reviewsTotal()
                this.setState({ modalReviews: data, modalPage: 1, modalHasMore: data.length < expected, modalLoading: false, modalFiltering: false })
            }
            else {
                this.setState({ modalLoading: false, modalFiltering: false })
            }
        })
    }

    /* Draw Load More Modal */
    _loadMoreModal = () => {
        if (this.state.modalLoading) {
            return
        }
        const nextPage = this.state.modalPage + 1
        this.setState({ modalLoading: true })
        const params = this.state.modalFilter ? { rating: this.state.modalFilter, page: nextPage } : { page: nextPage }
        reviews(this.state.organization.id, params).then((response) => {
            if (response && response.status === 200) {
                const incoming = Array.isArray(response.data.data) ? response.data.data : []
                const merged = [...this.state.modalReviews, ...incoming]
                const expected = this.state.modalFilter ? (this._distribution()[this.state.modalFilter] || 0) : this._reviewsTotal()
                this.setState({ modalReviews: merged, modalPage: nextPage, modalHasMore: merged.length < expected, modalLoading: false })
            }
            else {
                this.setState({ modalLoading: false })
            }
        })
    }

    /* Draw Open Review Photos */
    _openReviewPhotos = (photos, at) => {
        this.setState({ reviewPhotos: photos, reviewPhotoAt: at })
    }

    /* Draw Render Review */
    _renderReview = (review, index) => {
        const photos = Array.isArray(review.photos) ? review.photos : []
        return (
            <div key={`${index}`} className="review">
                <span className="avatar">{(review.name || "?")[0]}</span>
                <div>
                    <div className="review__head">
                        <div className="review__meta">
                            <span className="review__name">{review.name}</span>
                            <Stars value={review.rating} />
                        </div>
                        <span className="review__date">{utils.reviewDate(review.date)}</span>
                    </div>
                    {review.master && <div className="review__master">{translate("Специалист")}: <b>{review.master}</b></div>}
                    <p className="review__text">{review.text}</p>
                    {photos.length > 0 && (
                        <div className="myreview__photos" style={{ marginTop: 12, marginBottom: 0, flexWrap: "wrap" }}>
                            {photos.map((src, photoIndex) => (
                                <img key={`${photoIndex}`} src={utils.media(src)} alt="" onError={utils.onImgError} style={{ cursor: "pointer" }} onClick={() => this._openReviewPhotos(photos, photoIndex)} />
                            ))}
                        </div>
                    )}
                    {review.reply && (
                        <div className="review__reply">
                            <div className="review__reply-head">
                                <span className="review__reply-label">{translate("Ответ организации")}</span>
                                {review.replyDate && <span className="review__reply-date">{utils.reviewDate(review.replyDate)}</span>}
                            </div>
                            <p className="review__reply-text">{review.reply}</p>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    /* Draw Service Row */
    _serviceRow = (svc, keyPrefix) => {
        const isSelected = this.state.selected.includes(svc.id)
        return (
            <button key={`${keyPrefix}-${svc.id}`} className={`svc-row ${isSelected ? "is-selected" : ""}`} onClick={() => this._toggleService(svc.id)} style={{ width: "100%", textAlign: "left" }}>
                <div>
                    <div className="svc-row__name">{svc.name}</div>
                    <div className="svc-row__meta"><span>{svc.duration}</span></div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div className="svc-row__price">
                        {utils.price(svc)}
                        {utils.hasDiscount(svc) && <span style={{ textDecoration: "line-through", color: "var(--muted)", fontWeight: 400, marginLeft: 6 }}>{utils.money(svc.price)}</span>}
                    </div>
                    <span className="svc-row__add">{isSelected ? <Icon name="check" size={14} /> : <Icon name="plus" size={14} />}</span>
                </div>
            </button>
        )
    }

    /* Draw Selected Services */
    _selectedServices = () => this.state.services.filter((svc) => this.state.selected.includes(svc.id))

    /* Draw Total */
    _total = () => this._selectedServices().reduce((sum, svc) => sum + utils.effectivePrice(svc), 0)

    render = () => {
        if (this.state.loading) {
            return <Block><div className="page"><div className="container"><Loading /></div></div></Block>
        }
        if (this.state.error || !this.state.organization) {
            return <Block><div className="page"><div className="container"><Network onRetry={this.load} /></div></div></Block>
        }

        const s = this.state.organization
        const fav = this._isFav()
        const gallery = Array.isArray(s.gallery) && s.gallery.length ? s.gallery : []
        const queueAhead = s.queue ? (s.queueCount || 0) : 0
        const queuePosition = this.state.queuePosition

        /* Working hours — real schedule aggregated by the backend */
        const dayLabels = { monday: "Пн", tuesday: "Вт", wednesday: "Ср", thursday: "Чт", friday: "Пт", saturday: "Сб", sunday: "Вс" }
        const hours = s.hours || {}
        const today = hours.today || null
        const hasSchedule = Array.isArray(hours.schedule) && hours.schedule.some((day) => !day.dayOff)
        let todayText = translate("Часы не указаны")
        if (today) {
            todayText = s.openNow ? `${translate("Открыто · до")} ${today.close}` : `${translate("Закрыто · сегодня")} ${today.open} – ${today.close}`
        }
        else if (hasSchedule) {
            todayText = translate("Сегодня выходной")
        }
        const summary = this.state.reviews.summary || {}
        const selected = this._selectedServices()
        const total = this._total()
        const allFixed = selected.every((svc) => svc.priceKind === "fixed")

        /* Map — use real coordinates when the organization has them, otherwise fall back to a text lookup */
        const hasCoords = s.lat && s.lng
        const mapQuery = encodeURIComponent(`${s.name} ${s.addr || ""}`.trim())
        const mapSource = hasCoords
            ? `https://yandex.ru/map-widget/v1/?ll=${s.lng}%2C${s.lat}&z=16&pt=${s.lng}%2C${s.lat}%2Cpm2rdm&lang=ru_RU`
            : `https://yandex.ru/map-widget/v1/?text=${mapQuery}&z=16&lang=ru_RU`
        const mapLink = hasCoords
            ? `https://yandex.ru/maps/?ll=${s.lng}%2C${s.lat}&z=16&pt=${s.lng}%2C${s.lat}`
            : `https://yandex.ru/maps/?text=${mapQuery}`
        const routeLink = hasCoords
            ? `https://yandex.ru/maps/?rtext=~${s.lat}%2C${s.lng}&rtmode=auto`
            : `https://yandex.ru/maps/?mode=routes&rtext=~${mapQuery}`

        /* Social networks — only the ones the organization actually filled in.
           A stored value can be a full url or a bare handle/number; build a link for either. */
        const asUrl = (value, base) => value.startsWith("http") ? value : `${base}${value.replace(/^@/, "")}`
        const socialDefs = [
            { key: "instagram", icon: "instagram", label: "Instagram", link: (value) => asUrl(value, "https://instagram.com/") },
            { key: "tiktok", icon: "tiktok", label: "TikTok", link: (value) => asUrl(value, "https://tiktok.com/@") },
            { key: "whatsapp", icon: "whatsapp", label: "WhatsApp", link: (value) => value.startsWith("http") ? value : `https://wa.me/${value.replace(/\D/g, "")}` },
            { key: "telegram", icon: "telegram", label: "Telegram", link: (value) => asUrl(value, "https://t.me/") },
            { key: "threads", icon: "threads", label: "Threads", link: (value) => asUrl(value, "https://threads.net/@") },
            { key: "facebook", icon: "facebook", label: "Facebook", link: (value) => asUrl(value, "https://facebook.com/") },
            { key: "youtube", icon: "youtube", label: "YouTube", link: (value) => asUrl(value, "https://youtube.com/@") }
        ]
        const socials = socialDefs
            .filter((item) => s[item.key])
            .map((item) => ({ ...item, url: item.link(String(s[item.key])) }))

        const paymentMethods = Array.isArray(s.paymentMethods) ? s.paymentMethods.filter(Boolean) : []
        const amenities = Array.isArray(s.amenities) ? s.amenities.filter(Boolean) : []

        return (
            <Block>
                <div className="page">
                    <div className="container">
                        <div className="breadcrumb">
                            <button className="back" onClick={() => this.props.navigate("/search")} style={{ width: 32, height: 32 }}><Icon name="chevron_left" size={16} /></button>
                            <a onClick={() => this.props.navigate("/")} style={{ cursor: "pointer" }}>{translate("Главная")}</a>
                            <Icon name="chevron_right" size={12} />
                            <a onClick={() => this.props.navigate(s.category ? `/search?category=${s.category.id}` : "/search")} style={{ cursor: "pointer" }}>{s.category ? translate(s.category.name) : translate("Каталог")}</a>
                            <Icon name="chevron_right" size={12} />
                            <span className="breadcrumb__current">{s.name}</span>
                        </div>

                        <div className="organization-top">
                            <div className="organization-info">
                                <h1>{s.name}</h1>
                                {s.addr && <div className="organization-maddr">{s.addr}</div>}
                                <div className="organization-meta">
                                    <span className="organization-meta__rating"><Icon name="star" size={16} fill="currentColor" stroke="none" /> {s.rating ? Number(s.rating).toFixed(1) : "—"}</span>
                                    <span className="organization-meta__reviews">{s.reviewsCount || 0} {utils.plural(s.reviewsCount || 0, ["отзыв", "отзыва", "отзывов"])}</span>
                                    {(today || hasSchedule) ? (
                                        <>
                                            <span className="organization-meta__sep">·</span>
                                            <button className="organization-open" onClick={() => hasSchedule && this.setState({ hoursOpen: !this.state.hoursOpen })} style={{ color: s.openNow ? "black" : "var(--muted)", fontWeight: 600 }}>
                                                {s.openNow ? `${translate("Открыто до")} ${today.close}` : translate("Закрыто")}
                                                {hasSchedule && <span className="organization-open__chev" style={{ transform: this.state.hoursOpen ? "rotate(180deg)" : "none" }}><Icon name="chevron_down" size={14} /></span>}
                                            </button>
                                        </>
                                    ) : null}
                                    {s.distance ? <><span className="organization-meta__sep">·</span><span>{s.distance} {translate("м от вас")}</span></> : null}
                                </div>

                                {/* Mobile — expandable weekly schedule (hidden desktop; desktop uses the details card) */}
                                {hasSchedule && this.state.hoursOpen && (
                                    <div className="organization-mhours">
                                        {hours.schedule.map((day) => (
                                            <div key={day.day} className="organization-mhours__row">
                                                <span>{translate(dayLabels[day.day])}</span>
                                                <span style={{ fontWeight: day.dayOff ? 400 : 600, color: day.dayOff ? "var(--muted)" : "var(--ink)" }}>{day.dayOff ? translate("Выходной") : `${day.open} – ${day.close}`}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Mobile action row — matches the design (Позвонить / На карте / соцсети) */}
                                <div className="organization-mactions">
                                    <button className="btn" disabled={this.state.phoneLoading} onClick={this._call}>
                                        <Icon name="phone" size={16} /> {translate("Позвонить")}
                                    </button>
                                    <button className="btn btn--ghost" onClick={() => this.setState({ mapOpen: true })}>
                                        <Icon name="map" size={16} /> {translate("На карте")}
                                    </button>
                                    {/* Only show icons for networks the organization actually filled in */}
                                    {socials.map((social) => (
                                        <a key={social.key} className="organization-social" href={social.url} target="_blank" rel="noreferrer" aria-label={social.label}><Icon name={social.icon} size={18} /></a>
                                    ))}
                                </div>

                                <div className="organization-details" style={{ marginTop: 16 }}>
                                    <div className="organization-info__row organization-info__row--mdup">
                                        <Icon name="pin" size={20} />
                                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                                            <div>
                                                <div className="organization-info__row-label">{translate("Адрес")}</div>
                                                <div className="organization-info__row-value">{s.addr}</div>
                                            </div>
                                            <button className="btn btn--ghost" style={{ padding: "8px 14px", fontSize: 13, flex: "0 0 auto" }} onClick={() => this.setState({ mapOpen: true })}>
                                                <Icon name="map" size={14} /> {translate("На карте")}
                                            </button>
                                        </div>
                                    </div>
                                    {s.phone && (
                                        <div className="organization-info__row organization-info__row--mdup">
                                            <Icon name="phone" size={20} />
                                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                                                <div>
                                                    <div className="organization-info__row-label">{translate("Телефон")}</div>
                                                    <div className="organization-info__row-value">{this.state.phoneShown ? this.state.phoneFull : s.phone}</div>
                                                </div>
                                                {this.state.phoneShown ? (
                                                    <a href={`tel:${(this.state.phoneFull || "").replace(/\D/g, "")}`} className="btn" style={{ padding: "8px 14px", fontSize: 13, flex: "0 0 auto", color: "var(--on-accent)" }}>
                                                        <Icon name="phone" size={14} /> {translate("Позвонить")}
                                                    </a>
                                                ) : (
                                                    <button className="btn" disabled={this.state.phoneLoading} style={{ padding: "8px 14px", fontSize: 13, flex: "0 0 auto", color: "var(--on-accent)", opacity: this.state.phoneLoading ? 0.6 : 1 }} onClick={this._revealPhone}>
                                                        <Icon name="phone" size={14} /> {this.state.phoneLoading ? translate("Загрузка…") : translate("Показать номер")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="organization-info__row" style={{ borderBottom: 0 }}>
                                        <Icon name="clock" size={20} />
                                        <div style={{ flex: 1 }}>
                                            <div className="organization-info__row-label">{translate("Часы работы")}</div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                                <div className="organization-info__row-value">{todayText}</div>
                                                {hasSchedule && (
                                                    <button onClick={() => this.setState({ hoursOpen: !this.state.hoursOpen })} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--muted)", fontSize: 13, flex: "0 0 auto" }}>
                                                        {this.state.hoursOpen ? translate("Свернуть") : translate("Все дни")}
                                                        <span style={{ display: "inline-flex", transform: this.state.hoursOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}><Icon name="chevron_down" size={14} /></span>
                                                    </button>
                                                )}
                                            </div>
                                            {this.state.hoursOpen && hasSchedule && (
                                                <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
                                                    {hours.schedule.map((day) => (
                                                        <div key={day.day} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: day.dayOff ? "var(--muted)" : "var(--ink)" }}>
                                                            <span>{translate(dayLabels[day.day])}</span>
                                                            <span style={{ fontWeight: day.dayOff ? 400 : 600 }}>{day.dayOff ? translate("Выходной") : `${day.open} – ${day.close}`}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {(gallery.length > 0 || s.name) && (
                                <div className={`organization-hero${gallery.length <= 1 ? " organization-hero--single" : ""}`} style={{ cursor: gallery.length ? "pointer" : "default" }} onClick={() => gallery.length && this.setState({ galleryAt: 0 })}>
                                    <img className="organization-hero__main" src={utils.media(gallery[0]) || utils.FALLBACK_IMG} alt={s.name} onError={utils.onImgError} />
                                    {gallery[1] && <img src={utils.media(gallery[1]) || utils.FALLBACK_IMG} alt="" onError={utils.onImgError} onClick={(e) => { e.stopPropagation(); this.setState({ galleryAt: 1 }) }} />}
                                    {gallery[2] && <img src={utils.media(gallery[2]) || utils.FALLBACK_IMG} alt="" onError={utils.onImgError} onClick={(e) => { e.stopPropagation(); this.setState({ galleryAt: 2 }) }} />}
                                    {gallery.length > 0 && (
                                        <button className="organization-hero__more" onClick={(e) => { e.stopPropagation(); this.setState({ galleryAt: 0 }) }}>
                                            <Icon name="image" size={14} /> {translate("Все фото")} · {gallery.length}
                                        </button>
                                    )}
                                    <div className="organization-actions" onClick={(e) => e.stopPropagation()}>
                                        <button aria-label={translate("Поделиться")} onClick={this._share}><Icon name="share" size={16} /></button>
                                        <button className={fav ? "is-active" : ""} onClick={this._favourite} style={{ color: fav ? "var(--danger)" : undefined }} aria-label={translate("В избранное")}>
                                            <Icon name="heart" size={16} fill={fav ? "currentColor" : "none"} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="organization-grid">
                            <div className="organization-info">
                                {s.queue && (
                                    <div className="queue-banner">
                                        <span className="queue-banner__num">{queueAhead}</span>
                                        <div>
                                            <div className="queue-banner__label">{translate("человек в живой очереди")}</div>
                                            <div className="queue-banner__wait"><Icon name="clock" size={13} /> {translate("примерное ожидание")} ~{queueAhead * 15} {translate("мин")}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="tabs">
                                    {[
                                        { id: "services", label: "Услуги" },
                                        { id: "info", label: "Информация" },
                                        { id: "specs", label: "Специалисты" },
                                        { id: "reviews", label: "Отзывы", count: s.reviewsCount || 0 }
                                    ].map((t) => (
                                        <button key={t.id} className={`tab ${this.state.tab === t.id ? "is-active" : ""}`} onClick={() => this.setState({ tab: t.id })}>{translate(t.label)}{t.count != null ? ` · ${t.count}` : ""}</button>
                                    ))}
                                </div>

                                {this.state.tab === "services" && (
                                    (this.state.serviceType === "category" && this.state.serviceGroups.length > 0) ? (
                                        <div>
                                            {this.state.serviceGroups.map((group) => (
                                                <div key={group.id == null ? "other" : group.id} style={{ marginBottom: 18 }}>
                                                    <div className="svc-cat">{group.name}</div>
                                                    {group.services.map((svc) => this._serviceRow(svc, group.id == null ? "other" : group.id))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div>
                                            {this.state.services.map((svc) => this._serviceRow(svc, "all"))}
                                        </div>
                                    )
                                )}

                                {this.state.tab === "info" && (
                                    <div style={{ paddingTop: 8, lineHeight: 1.6, color: "var(--ink-2)" }}>
                                        <p>{s.about || `${s.name} ${translate("— проверенная организация. Удобная онлайн-запись и высокий рейтинг по отзывам клиентов.")}`}</p>

                                        {/* Способы оплаты — only when the organization set them */}
                                        {paymentMethods.length > 0 && (
                                            <div style={{ marginTop: 20 }}>
                                                <div className="organization-info__block-title">{translate("Способы оплаты")}</div>
                                                <div className="organization-chips">
                                                    {paymentMethods.map((item, index) => (
                                                        <span key={`${index}`} className="organization-chip"><Icon name="wallet" size={15} /> {translate(item)}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Удобства */}
                                        {amenities.length > 0 && (
                                            <div style={{ marginTop: 20 }}>
                                                <div className="organization-info__block-title">{translate("Удобства")}</div>
                                                <div className="organization-chips">
                                                    {amenities.map((item, index) => (
                                                        <span key={`${index}`} className="organization-chip"><Icon name="check" size={15} /> {translate(item)}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Мы в соцсетях — clickable links, only for the networks that are filled in */}
                                        {socials.length > 0 && (
                                            <div style={{ marginTop: 20 }}>
                                                <div className="organization-info__block-title">{translate("Мы в соцсетях")}</div>
                                                <div className="organization-chips">
                                                    {socials.map((social) => (
                                                        <a key={social.key} className="organization-chip organization-chip--link" href={social.url} target="_blank" rel="noreferrer"><Icon name={social.icon} size={15} /> {social.label}</a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {this.state.tab === "specs" && (
                                    <div className="spec-grid">
                                        {this.state.specialists && Array.isArray(this.state.specialists) && this.state.specialists.map((sp) => (
                                            <div key={`${sp.id}`} className="spec">
                                                <div className="spec__avatar">{sp.avatar ? <img src={utils.media(sp.avatar)} alt="" onError={utils.onAvatarError} /> : <Icon name="user" size={28} />}</div>
                                                <div className="spec__name">{sp.name}</div>
                                                {sp.profession && <div className="spec__profession">{sp.profession}</div>}
                                                <div className="spec__rating"><Icon name="star" size={12} fill="currentColor" stroke="none" /> {sp.rating || "—"}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {this.state.tab === "reviews" && (
                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                                            <div>
                                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 22, fontWeight: 800, letterSpacing: "-0.015em" }}>
                                                    <Icon name="star" size={20} fill="var(--star)" stroke="none" style={{ color: "var(--star)" }} />
                                                    {summary.rating ? Number(summary.rating).toFixed(1) : (s.rating ? Number(s.rating).toFixed(1) : "—")}
                                                </div>
                                                <div style={{ color: "var(--muted)", fontSize: 13 }}>{translate("Основано на")} {summary.total || s.reviewsCount || 0} {utils.plural(summary.total || s.reviewsCount || 0, ["отзыве", "отзывах", "отзывах"])}</div>
                                            </div>
                                            <button className="btn" style={{ whiteSpace: "nowrap", flex: "0 0 auto" }} onClick={this._openLeaveReview}>
                                                <Icon name={this.state.myReview ? "edit" : "plus"} size={14} /> {this.state.myReview ? translate("Изменить отзыв") : translate("Оставить отзыв")}
                                            </button>
                                        </div>
                                        {(this.state.reviews.data || []).slice(0, 3).map((r, index) => this._renderReview(r, index))}
                                        {(!this.state.reviews.data || this.state.reviews.data.length === 0) && (
                                            <div style={{ padding: "24px 0", color: "var(--muted)" }}>{translate("Отзывов пока нет.")}</div>
                                        )}
                                        {(summary.total || (this.state.reviews.data || []).length) > 3 && (
                                            <button className="btn btn--ghost btn--block" style={{ marginTop: 16 }} onClick={this._openReviews}>
                                                {translate("Все отзывы")} · {summary.total || this.state.reviews.data.length} {utils.plural(summary.total || this.state.reviews.data.length, ["отзыв", "отзыва", "отзывов"])}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className={`book-card ${(!s.queue && selected.length === 0) ? "book-card--empty" : ""}`}>
                                {s.queue ? (
                                    <>
                                        <h3 className="book-card__title">{translate("Живая очередь")}</h3>
                                        {queuePosition ? (
                                            <div className="queue">
                                                <div className="queue__num">{queuePosition.ahead != null ? queuePosition.ahead : Math.max(0, (queuePosition.position || 1) - 1)}</div>
                                                <div>
                                                    <div className="queue__label">{translate("перед вами · вы в очереди")}</div>
                                                    <div className="queue__wait"><Icon name="clock" size={13} /> ~{(queuePosition.etaMinutes != null ? queuePosition.etaMinutes : Math.max(0, (queuePosition.position || 1) - 1) * 15)} {translate("мин ожидания")}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="queue">
                                                <div className="queue__num">{queueAhead}</div>
                                                <div>
                                                    <div className="queue__label">{translate("человек впереди")}</div>
                                                    <div className="queue__wait"><Icon name="clock" size={13} /> ~{queueAhead * 15} {translate("мин ожидания")}</div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <h3 className="book-card__title">{translate("Ваша запись")}</h3>
                                )}
                                {selected.length === 0 ? (
                                    <p style={{ color: "var(--muted)", fontSize: 14, margin: 0 }}>{translate("Выберите услуги, чтобы продолжить")}</p>
                                ) : (
                                    <>
                                        {selected.map((svc) => (
                                            <div key={`${svc.id}`} className="book-card__row">
                                                <span>{svc.name}</span>
                                                <span style={{ fontWeight: 700 }}>
                                                    {utils.price(svc)}
                                                    {utils.hasDiscount(svc) && <span style={{ textDecoration: "line-through", color: "var(--muted)", fontWeight: 400, marginLeft: 6 }}>{utils.money(svc.price)}</span>}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="book-card__row book-card__row--total">
                                            <span>{translate("Итого")}</span>
                                            <span>{total > 0 ? (allFixed ? utils.money(total) : translate("от") + " " + utils.money(total)) : translate("По запросу")}</span>
                                        </div>
                                    </>
                                )}
                                {s.queue ? (
                                    queuePosition ? (
                                        <button className="btn btn--danger-ghost btn--block btn--lg" style={{ marginTop: 16 }} disabled={this.state.queueBusy} onClick={this._leaveQueue}>
                                            {this.state.queueBusy ? translate("Выходим…") : translate("Покинуть очередь")}
                                        </button>
                                    ) : (
                                        <button className="btn btn--block btn--lg" style={{ marginTop: 16 }} disabled={this.state.queueBusy || selected.length === 0} onClick={this._joinQueue}>
                                            {this.state.queueBusy ? translate("Встаём…") : translate("Встать в очередь")}
                                        </button>
                                    )
                                ) : (
                                    <button className="btn btn--block btn--lg" style={{ marginTop: 16 }} disabled={selected.length === 0} onClick={this._book}>
                                        {translate("Записаться")}
                                    </button>
                                )}
                                <div className="book-card__hint">{s.queue ? (queuePosition ? translate("Вы в очереди · уведомим, когда подойдёт") : translate("Бесплатно · уведомим, когда подойдёт очередь")) : translate("Бесплатно · подтверждение за 5 минут")}</div>
                            </div>
                        </div>
                    </div>

                    {this.state.galleryAt >= 0 && gallery.length > 0 && (
                        <Lightbox images={gallery} startIndex={this.state.galleryAt} title={`${s.name} · ${translate("фото")}`} onClose={() => this.setState({ galleryAt: -1 })} />
                    )}

                    {this.state.mapOpen && (
                        <div className="modal-backdrop" onClick={() => this.setState({ mapOpen: false })}>
                            <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
                                <div className="modal__header">
                                    <h3 className="modal__title">{translate("На карте")}</h3>
                                    <button className="modal__close" onClick={() => this.setState({ mapOpen: false })} aria-label={translate("Закрыть")}><Icon name="close" size={20} /></button>
                                </div>
                                <div className="modal__body" style={{ padding: 0, overflow: "hidden" }}>
                                    <iframe title={translate("Карта")} className="map-canvas" src={mapSource} loading="lazy"></iframe>
                                    <div className="map-bar">
                                        <div className="map-bar__addr">
                                            <Icon name="pin" size={18} />
                                            <div>
                                                <b>{s.name}</b>
                                                {s.addr}
                                            </div>
                                        </div>
                                        <div className="map-bar__actions">
                                            <a className="btn btn--ghost" href={routeLink} target="_blank" rel="noopener noreferrer" aria-label={translate("Маршрут")}><Icon name="map" size={14} /> <span className="map-bar__label">{translate("Маршрут")}</span></a>
                                            <a className="btn" href={mapLink} target="_blank" rel="noopener noreferrer" style={{ color: "var(--on-accent)" }} aria-label={translate("На карте")}><Icon name="pin" size={14} /> <span className="map-bar__label">{translate("На карте")}</span></a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {this.state.leaveOpen && (
                        <LeaveReview
                            name={s.name}
                            token={this.props.token}
                            specialists={this.state.specialists}
                            editing={!!this.state.myReview}
                            initial={this.state.myReview ? {
                                rating: this.state.myReview.rating,
                                text: this.state.myReview.text,
                                masterId: this.state.myReview.masterId,
                                photos: (this.state.myReview.photos || []).map((path) => ({ id: path.split("/").pop(), url: utils.media(path) }))
                            } : null}
                            onClose={() => this.setState({ leaveOpen: false })}
                            onSubmit={this._submitReview}
                        />
                    )}

                    {this.state.reviewsOpen && (() => {
                        const total = summary.total || s.reviewsCount || 0
                        const score = summary.rating ? Number(summary.rating).toFixed(1) : (s.rating ? Number(s.rating).toFixed(1) : "—")
                        const distribution = summary.distribution || {}
                        return (
                            <div className="modal-backdrop" onClick={() => this.setState({ reviewsOpen: false })}>
                                <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
                                    <div className="modal__header">
                                        <h3 className="modal__title">{translate("Отзывы")} · {s.name}</h3>
                                        <button className="modal__close" onClick={() => this.setState({ reviewsOpen: false })} aria-label={translate("Закрыть")}><Icon name="close" size={20} /></button>
                                    </div>
                                    <div className="modal__body" style={{ overflowY: "auto" }}>
                                        <div className="rating-summary">
                                            <div className="rating-score">
                                                <div className="rating-score__num">{score}</div>
                                                <div className="rating-score__stars"><Stars value={summary.rating || s.rating || 0} size={20} /></div>
                                                <div className="rating-score__count">{total} {utils.plural(total, ["отзыв", "отзыва", "отзывов"])}</div>
                                            </div>
                                            <div className="rating-bars">
                                                {[5, 4, 3, 2, 1].map((star) => {
                                                    const count = distribution[star] || 0
                                                    const percent = total ? Math.round((count / total) * 100) : 0
                                                    return (
                                                        <div key={star} className="rating-bar">
                                                            <span className="rating-bar__label">{star} <Icon name="star" size={13} fill="currentColor" stroke="none" /></span>
                                                            <span className="rating-bar__track"><span className="rating-bar__fill" style={{ width: `${percent}%` }} /></span>
                                                            <span className="rating-bar__count">{count}</span>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>

                                        <div className="review-filters">
                                            <button className={`rchip ${this.state.modalFilter === null ? "is-active" : ""}`} disabled={this.state.modalLoading} onClick={() => this._filterReviews(null)}>{translate("Все")} · {total}</button>
                                            {[5, 4, 3, 2, 1].map((star) => (
                                                <button key={star} className={`rchip ${this.state.modalFilter === star ? "is-active" : ""}`} disabled={this.state.modalLoading} onClick={() => this._filterReviews(star)}>{star} <Icon name="star" size={13} fill="currentColor" stroke="none" /></button>
                                            ))}
                                            <span className="review-filters__spacer" />
                                            <button className="btn" style={{ whiteSpace: "nowrap", flex: "0 0 auto" }} onClick={this._openLeaveReview}>
                                                <Icon name={this.state.myReview ? "edit" : "plus"} size={14} /> {this.state.myReview ? translate("Изменить отзыв") : translate("Оставить отзыв")}
                                            </button>
                                        </div>

                                        {this.state.modalFiltering ? (
                                            <Loading inline />
                                        ) : (
                                            <>
                                                {this.state.modalReviews.map((r, index) => this._renderReview(r, index))}
                                                {this.state.modalReviews.length === 0 && (
                                                    <div style={{ padding: "24px 0", color: "var(--muted)" }}>{translate("Отзывов с такой оценкой пока нет.")}</div>
                                                )}
                                                {this.state.modalHasMore && (
                                                    <button className="btn btn--ghost btn--block" style={{ margin: "16px 0 24px" }} disabled={this.state.modalLoading} onClick={this._loadMoreModal}>
                                                        {this.state.modalLoading ? translate("Загрузка…") : translate("Показать ещё")}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })()}

                    {this.state.reviewPhotos && this.state.reviewPhotos.length > 0 && (
                        <Lightbox images={this.state.reviewPhotos} startIndex={this.state.reviewPhotoAt} title={translate("Фото из отзыва")} onClose={() => this.setState({ reviewPhotos: null })} />
                    )}

                    {this.state.toast && (
                        <div className="toast">
                            <div className="toast__check"><Icon name="check" size={16} /></div>
                            <div>{this.state.toast}</div>
                        </div>
                    )}
                </div>
            </Block>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        favourites: state.favourites,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data }),
        removeToken: () => { dispatch({ type: "REMOVE_TOKEN" }); dispatch({ type: "REMOVE_USER" }) },
        addFavourite: data => dispatch({ type: "ADD_FAVOURITE", payload: data }),
        removeFavourite: data => dispatch({ type: "REMOVE_FAVOURITE", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Organization))
